/* eslint-disable @typescript-eslint/ban-ts-comment */
import { GetStaticPaths, GetStaticProps } from "next";
import Image from "next/image";
import React, { useMemo, useRef } from "react";
import { ReactSVG } from "react-svg";

import { BreadcrumbsHeader } from "@/components/BreadcrumbsHeader";
import CategoryHeader from "@/components/CategoryHeader/CategoryHeader";
import { ContainerInformativo } from "@/components/Container/ContainerInformativo";
import EmptyProductsList from "@/components/EmptyProductsList/EmptyProductsList";
import { InformativeAccumulationPartner } from "@/components/InformativeAccumulationPartner";
import { InformativePartner } from "@/components/InformativePartner";
import Login from "@/components/Login/Login";
import { LoginRef } from "@/components/Login/types";
import { ProductDetails } from "@/components/ProductDetails";
import { RedeemPartner } from "@/components/RedeemPartner";
import { DefaultRedeemPartnerCard } from "@/components/RedeemPartner/DefaultRedeemPartnerCarousel/DefaultRedeemPartnerCard";
import { CategoryService } from "@/services/api/categoryService";
import { ProductItem } from "@/services/api/productsServices.api";
import { CategoriesService, CategoryConfig, PartnerConfig } from "@/services/contentful/categories";
import { PartnersInformationPageConfig, PartnersInformationPageService } from "@/services/contentful/partnersInformationPage";
import { PlatformConfigurationService } from "@/services/contentful/platformConfiguration";
import theme from "@/styles/themes/theme";
import { RegulationConfig } from "@/types/contentful";
import { Document } from "@contentful/rich-text-types";
import { css } from "@emotion/react";
import { Grid, Stack, Typography } from "@mui/material";
import { House } from "@phosphor-icons/react";

import Parceiros from "../parceiro/[partnerId]";
import { FootballApi } from "@/services/api/football.api";
import { FootballCardProps } from "@/components/Carousel/CarouselFootball/FootballCard/types";
import { Constants } from "@/config";
import { DefaultRedeemMilesPartnerCard } from "@/components/RedeemPartner/DefaultRedeemMilesPartnerCard/DefaultRedeemMilesPartnerCard";

const styles = {
  icon: css`
    height: 20px;
    & svg {
      fill: ${theme.palette.grey[600]};
      height: 20px;
      width: 20px;
    }
  `,
  inforimage: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin-top: 0;
  `,
  image: css`
    width: 100% !important;
    max-height: 400px;
    height: auto !important;
    object-fit: cover;
  `
};

interface Props {
  products: ProductItem[];
  category: CategoryConfig;
  partner: PartnerConfig;
  regulation: RegulationConfig | null;
  product: ProductItem | null;
  productId: number;
  itemSwapInstructions: Document;
  partnersInformationPage: PartnersInformationPageConfig | null;
  footballCarouselItems: FootballCardProps[];
}

const Categoria: React.FC<Props> = ({
  products,
  category,
  partner,
  product,
  productId,
  regulation,
  itemSwapInstructions,
  partnersInformationPage,
  footballCarouselItems
}) => {
  const loginModalRef = useRef<LoginRef>(null);
  const isMilesPartnerCategory =
    category.partners.length > 0 && category.partners.some((partner) => partner.partnerType == Constants.partnerTypeEnum.milhas_pontos);

  const Component = useMemo(() => {
    /* Página de acúmulo */
    if (partner?.accumulationInformative) {
      return (
        <InformativeAccumulationPartner
          partnerId={partner.partnerId}
          logo={partner.partnerLogo}
          accumulationPartner={partner?.accumulationInformative}
          partnerName={partner.name}
        />
      );
    }

    /* Página de acúmulo antigo (Sérgio) */
    if (partner?.informative && partnersInformationPage?.header) {
      return <Parceiros partnersInformationPage={partnersInformationPage} />;
    }

    /* Página de informativo (novo) */
    if (partner?.informativeNew) {
      return <InformativePartner informativePartner={partner.informativeNew} footballCarouselItems={footballCarouselItems} />;
    }

    /* Página de informativo (antigo) */
    if (partner?.informative) {
      return <ContainerInformativo informative={partner.informative} />;
    }

    return null;
  }, [
    footballCarouselItems,
    partner?.accumulationInformative,
    partner.informative,
    partner.informativeNew,
    partner.name,
    partner.partnerId,
    partner.partnerLogo,
    partnersInformationPage
  ]);

  return (
    <>
      <BreadcrumbsHeader
        titleHeader={category?.name}
        regulationPdf={regulation?.document}
        regulationHtml={regulation?.htmlRegulation}
        allowDownload={regulation?.allowDownload}
        partnerName={partner?.name}
        items={[
          {
            title: "Todas as Categorias",
            link: "/categorias",
            icon: <House size={18} color={theme.palette.common.black} />
          },
          {
            title: category?.name,
            link: `/categoria/${category?.categoryId}`,
            icon: category?.icon?.endsWith(".svg") ? (
              <ReactSVG src={category.icon} css={styles.icon} />
            ) : (
              <Image src={category.icon} alt={category?.name} width={20} height={20} />
            )
          },
          {
            title: (
              <Typography color="#0045B5" component="span">
                {partner?.name}
              </Typography>
            ),
            link: `/categoria/${category?.categoryId}/parceiro/${partner?.partnerId}`
          }
        ]}
      />

      {Component ?? (
        <>
          {category?.specialOffer && !partner?.partnerId && !productId && (
            <Stack sx={{ width: "100%", height: "auto" }} css={styles.inforimage}>
              <Image src={category.specialOffer} alt="Banner promoção" width={1440} height={306} css={styles.image} />
            </Stack>
          )}

          {partner?.redeemPartner && !productId ? (
            <RedeemPartner redeemPartner={partner?.redeemPartner} categoryName={category?.name} partnerName={partner?.name ?? ""} />
          ) : (
            <Stack
              direction="row"
              alignItems="flex-end"
              sx={{
                py: 4,
                px: { lg: 16, md: 4, xs: 2 }
              }}
            >
              <Stack flex={1}>
                <CategoryHeader title={category?.listProductsPageTitle} subtitle={category?.listProductsPageSubtitle} />

                {productId ? (
                  <ProductDetails
                    {...(product as ProductItem)}
                    imageUrl={product?.imageUrl as string}
                    categoryId={category?.categoryId}
                    partnerId={partner?.partnerId}
                    productId={productId}
                    itemSwapInstructions={itemSwapInstructions}
                  />
                ) : (
                  <>
                    {!products?.length && !isMilesPartnerCategory ? <EmptyProductsList /> : null}

                    <Grid container justifyContent="center" spacing={4} py={4}>
                      {isMilesPartnerCategory
                        ? category.partners?.map((partner, index) => (
                            <Grid item key={index}>
                              <DefaultRedeemMilesPartnerCard
                                image={partner.partnerLogo}
                                categoryId={category?.categoryId || ""}
                                partnerName={partner?.name || ""}
                                partnerId={partner?.partnerId || ""}
                              />
                            </Grid>
                          ))
                        : products?.map((product, index) => (
                            <Grid item key={index}>
                              <DefaultRedeemPartnerCard
                                card={{
                                  image: product?.imageUrl,
                                  productId: productId || product?.externalCode,
                                  partner: { id: partner?.partnerId || product.partnerId, name: partner?.name || product.title },
                                  category: { id: category?.categoryId || product.categoryId, name: category?.name },
                                  isInformative: product?.isInformative,
                                  redemptionUrl: product?.redemptionUrl,
                                  title: product?.description,
                                  points: product?.productCoinValue,
                                  buttonTitle: product?.buttonTitle
                                }}
                                parentPartnerName={partner?.name}
                              />
                            </Grid>
                          ))}
                    </Grid>
                  </>
                )}
              </Stack>
            </Stack>
          )}
        </>
      )}

      <Login ref={loginModalRef} />
    </>
  );
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slugs = (params?.slug as string[]) ?? [];

  const [props, { menuItems, simulatorAccumulationInformativeText, locationCompany }] = await Promise.all([
    CategoryService.getCategoryProps({ slugs }),
    PlatformConfigurationService.getData()
  ]);

  const { partner } = props as { partner: PartnerConfig };

  const [partnerData, partnersInformationPage, footballCarouselItems] = await Promise.all([
    CategoriesService.getPartnerById({ partnerId: partner.partnerId }),
    PartnersInformationPageService.partnersInformationPage({ partnerId: partner.partnerId }),
    FootballApi.listMatchs()
  ]);

  const { itemSwapInstructions } = partnerData.partner;

  Object.assign(props, {
    menuItems,
    itemSwapInstructions,
    simulatorAccumulationInformativeText,
    locationCompany,
    partnersInformationPage,
    footballCarouselItems
  });

  return {
    props,
    revalidate: 1800 // 30 min
  };
};

export const getStaticPaths = (async () => {
  return {
    paths: [],
    fallback: "blocking"
  };
}) satisfies GetStaticPaths;

export default Categoria;
